// Small helpers for text shown to people.

/**
 * Up to two letters standing for a name: the first letter of its first two words, or the first two
 * letters of a single word. `?` when it has no letter or digit.
 */
export function initials(text: string): string {
  const words = text.split(/[^\p{L}\p{N}]+/u).filter((word) => word !== "");
  if (words.length === 0) return "?";
  const letters =
    words.length === 1 ? [...words[0]].slice(0, 2) : words.slice(0, 2).map((word) => [...word][0]);
  return letters.join("").toUpperCase();
}

/** A text split into the emoji it starts with, if any, and the rest. */
export interface LeadingEmojiSplit {
  emoji: string | null;
  rest: string;
}

// One emoji (with its skin tone, variation selector or ZWJ-joined parts), a space, then more text.
const EMOJI = String.raw`\p{Extended_Pictographic}[\p{Emoji_Modifier}️]*`;
const LEADING_EMOJI = new RegExp(String.raw`^(${EMOJI}(?:‍${EMOJI})*)\s+(\S.*)$`, "us");

/**
 * Splits a leading emoji off a text. A text that does not start with an emoji and a space, or
 * that is nothing but an emoji, has none and comes back whole.
 */
export function splitLeadingEmoji(text: string): LeadingEmojiSplit {
  const match = LEADING_EMOJI.exec(text.trim());
  return match ? { emoji: match[1], rest: match[2] } : { emoji: null, rest: text };
}

/** A count with its noun, the noun made plural when the count is not one: "1 claim", "3 claims". */
export function formatCount(count: number, noun: string): string {
  return `${count.toLocaleString()} ${noun}${count === 1 ? "" : "s"}`;
}
